import React, { useCallback, useEffect, useMemo, useState } from 'react';
import useAuth from '../../auth/UseAuth';
import {
  AuditSampleTracking,
  AuditTransitionData,
  AuditTransitionSample,
  FromTransitionDto,
  GetAuditsForResearchProject,
} from '../../data/SampleTrackingAuditData';
import { LoadingState, LoadState } from '../../components/LoadingStateUtil';
import {
  DataGridPro,
  FilterColumnsArgs,
  GetColumnForNewFilterArgs,
  GRID_TREE_DATA_GROUPING_FIELD,
  GridColDef,
  GridRenderCellParams,
  GridRowsProp,
  GridToolbarFilterButton,
  getGridStringOperators,
} from '@mui/x-data-grid-pro';
import useMemoTranslation from '../../hooks/UseMemoTranslation';
import {
  auditAction,
  auditEvent,
  auditNumTransitionSamples,
  auditSampleTrackingId,
  auditType,
  copyBtn,
  transition,
  auditUser,
  auditTransitionSampleIdentifier,
  auditSampleId,
  auditChangeType,
  auditChangeDetails,
  auditParentSampleId,
  patientId,
  patientDeidentity,
  transitionId,
  auditTransitionDetails,
} from '../../util/Constants';
import { dateGetter, dateTimeFormatter } from '../../util/grid/TableUtils';
import { truncateGuid } from '../../data/SampleData';
import { LoadingIndicator } from '../../components/LoadingIndicator';
import { ErrorIndicator } from '../../components/ErrorIndicator';
import { Typography, Box } from '@mui/material';
import { renderCopyTextGridCell } from '../../components/grid/cell/CopyTextGridCell';
import { FlexBox } from '../../components/FlexBox';
import { AuditTypeBadge } from '../../components/AuditTypeBadges';
import { renderGuidCellTooltip } from '../../components/grid/cell/GuidGridCellTooltip';
import { ExpandableDetailsCell } from '../../components/grid/cell/ExpandableDetailsCell';

export interface SampleTrackingAuditsGridProps {
  researchProjectId: string;
}

type Row = {
  id: number;
  auditSampleTrackingId: string;
  transition?: string | undefined;
  transitionChanges?: AuditTransitionData | undefined;
  transitionId?: string | undefined;
  auditUser: string;
  auditEvent?: Date | undefined;
  auditType?: string | undefined;
  auditAction?: string | undefined;
  transitionSamples: ReadonlyArray<AuditTransitionSample>;
  numTransitionSamples: number;
};

type AuditDetailRow = {
  id: string;
  auditEvent?: Date | undefined;
  transitionSampleIdentifier?: string | undefined;
  parentSampleId?: string | undefined;
  sampleId?: string | undefined;
  sampleIdentifier?: string | undefined;
  patientId: string;
  patientDeidentity: string;
  parentRowId?: string | undefined;
  auditChangeType?: string | undefined;
  auditChangeDetails?: any | undefined;
};

// This component functions as the details panel for the audit record, which will show the user the details of the particular operation
export const AuditRecordDetails = ({ row }: { row: Row }) => {
  const { t } = useMemoTranslation();
  const [rows, setRows] = useState<GridRowsProp<AuditDetailRow>>([]);

  const useColumns = (): GridColDef[] => {
    return useMemo(
      () => [
        {
          field: copyBtn,
          headerName: t(copyBtn),
          headerAlign: 'center',
          align: 'center',
          maxWidth: 30,
          renderCell: renderCopyTextGridCell,
          sortable: false,
          valueGetter: params => params.row.transitionSampleIdentifier,
        },
        {
          field: auditTransitionSampleIdentifier,
          headerName: t(auditTransitionSampleIdentifier),
          headerAlign: 'left',
          align: 'left',
          width: 200,
        },
        {
          field: auditEvent,
          headerName: t(auditEvent),
          headerAlign: 'left',
          align: 'left',
          width: 220,
          maxWidth: 250,
          type: 'dateTime',
          valueFormatter: dateTimeFormatter,
          valueGetter: dateGetter,
        },
        {
          field: auditChangeType,
          headerName: t(auditChangeType),
          headerAlign: 'left',
          align: 'left',
          width: 100,
          maxWidth: 100,
          flex: 1,
          sortable: false,
          renderCell: (params: GridRenderCellParams) => <AuditTypeBadge type={params.value || 'Create'} />,
        },
        {
          field: auditSampleId,
          headerName: t(auditSampleId),
          headerAlign: 'left',
          align: 'left',
          width: 100,
          maxWidth: 150,
          valueFormatter: params => params.value && truncateGuid(params.value),
          renderCell: renderGuidCellTooltip,
          flex: 1,
        },
        {
          field: auditParentSampleId,
          headerName: t(auditParentSampleId),
          headerAlign: 'left',
          align: 'left',
          width: 100,
          maxWidth: 150,
          valueFormatter: params => params.value && truncateGuid(params.value),
          renderCell: renderGuidCellTooltip,
          flex: 1,
        },
        {
          field: patientId,
          headerName: t(patientId),
          headerAlign: 'left',
          align: 'left',
          width: 100,
          maxWidth: 150,
          valueFormatter: params => params.value && truncateGuid(params.value),
          renderCell: renderGuidCellTooltip,
          flex: 1,
        },
        {
          field: patientDeidentity,
          headerName: t(patientDeidentity),
          headerAlign: 'left',
          align: 'left',
          width: 200,
          flex: 1,
        },
        {
          field: auditChangeDetails,
          headerName: t(auditChangeDetails),
          headerAlign: 'left',
          align: 'left',
          minWidth: 200,
          sortable: false,
          flex: 1,
          renderCell: (params: GridRenderCellParams) => {
            // Format the JSON
            let content = '';
            try {
              content = JSON.stringify(params.value, null, 2); // pretty print
            } catch {
              content = String(params.value); // fallback
            }

            return <ExpandableDetailsCell value={content} />;
          },
        },
      ],
      []
    ) as GridColDef[];
  };

  const columns = useColumns();

  const getRows = useCallback((data: ReadonlyArray<AuditTransitionSample>) => {
    // this is the inner grid row data, which shows the specific changes for each transition sample in the audit record
    const rows: AuditDetailRow[] = data.map((r, i) => {
      const { auditChangeType, createdAt, auditTransitionSampleChangeId, ...changeData } =
        r.transitionSampleChangeData || {
          auditChangeType: undefined,
          createdAt: undefined,
          changeData: undefined,
          auditTransitionSampleChangeId: undefined,
        };

      return {
        id: `${i + 1}`,
        auditEvent: r.createdAt,
        transitionSampleIdentifier: r.transitionSampleIdentifier,
        parentSampleId: r.parentAuditTransitionSample?.sampleId,
        sampleId: r.sampleId,
        patientId: r.patientId,
        patientDeidentity: r.patientDeidentity,
        auditChangeType: auditChangeType,
        auditChangeDetails: changeData,
      };
    });

    return rows;
  }, []);

  useEffect(() => {
    const rows = getRows(row.transitionSamples);
    setRows(rows);

    return () => {
      setRows([]); // Clear rows when component unmounts
    };
  }, [getRows, row.transitionSamples]);

  return (
    <Box display='flex' flexDirection='row' alignItems='center' mb={1} justifyContent='space-between' padding={'10px'}>
      <DataGridPro
        columns={columns}
        rows={rows}
        getRowId={row => row.id}
        hideFooter={true}
        defaultGroupingExpansionDepth={0}
        getRowHeight={() => 'auto'} // This makes rows grow to fit vertically
        disableRowSelectionOnClick
        disableColumnReorder={true}
      />
    </Box>
  );
};

// This is the audit grid table, which shows the audit records at the top-level
// each row allows the user to expand and show additional details about the action
export const SampleTrackingAuditsGrid = ({ researchProjectId }: SampleTrackingAuditsGridProps) => {
  const { accessToken } = useAuth();
  const { t } = useMemoTranslation();

  const [loadingState, setLoadingState] = useState<LoadingState>({ status: 'NotStarted' });

  const useColumns = (): GridColDef[] => {
    return useMemo(
      () => [
        {
          field: auditSampleTrackingId,
          headerName: t(auditSampleTrackingId),
          headerAlign: 'left',
          align: 'left',
          width: 200,
          valueFormatter: ({ value }) => value && truncateGuid(value),
          renderCell: renderGuidCellTooltip,
        },
        {
          field: auditEvent,
          headerName: t(auditEvent),
          headerAlign: 'left',
          align: 'left',
          width: 250,
          type: 'dateTime',
          valueGetter: dateGetter,
          valueFormatter: dateTimeFormatter,
        },
        {
          field: auditAction,
          headerName: t(auditAction),
          headerAlign: 'left',
          align: 'left',
          minWidth: 100,
          maxWidth: 200,
          flex: 1,
          renderCell: (params: GridRenderCellParams) => <AuditTypeBadge type={params.value} />,
        },
        {
          field: auditType,
          headerName: t(auditType),
          headerAlign: 'left',
          align: 'left',
          minWidth: 100,
          maxWidth: 200,
          flex: 1,
          renderCell: (params: GridRenderCellParams) => <AuditTypeBadge type={params.value} />,
        },
        {
          field: auditUser,
          headerName: t(auditUser),
          headerAlign: 'left',
          align: 'left',
          minWidth: 150,
          maxWidth: 400,
          flex: 1,
        },
        {
          field: transition,
          headerName: t(transition),
          headerAlign: 'left',
          align: 'left',
          minWidth: 150,
          maxWidth: 250,
          flex: 1,
        },
        {
          field: transitionId,
          headerName: t(transitionId),
          headerAlign: 'left',
          align: 'left',
          width: 250,
          valueFormatter: ({ value }) => value && truncateGuid(value),
          renderCell: renderGuidCellTooltip,
        },
        {
          field: auditNumTransitionSamples,
          headerName: t(auditNumTransitionSamples),
          headerAlign: 'left',
          align: 'left',
          minWidth: 75,
          maxWidth: 250,
          flex: 1,
        },
        {
          field: auditTransitionDetails,
          headerName: t(auditTransitionDetails),
          headerAlign: 'left',
          align: 'left',
          minWidth: 200,
          sortable: false,
          flex: 1,
          renderCell: (params: GridRenderCellParams) => {
            // Format the JSON
            let content = '';

            if (!params.value || Object.keys(params.value).length === 0) {
              return null;
            }

            try {
              content = JSON.stringify(params.value, null, 2); // pretty print
            } catch {
              content = String(params.value); // fallback
            }

            return <ExpandableDetailsCell value={content} />;
          },
        },
        {
          field: auditTransitionSampleIdentifier,
          headerName: t(auditTransitionSampleIdentifier),
          filterable: true,
          filterOperators: getGridStringOperators().map(operator => ({
            ...operator,
            getApplyFilterFn: (filterItem, _) => {
              // @ts-ignore
              const defaultFn = operator.getApplyFilterFn?.(filterItem, undefined as GridColDef);
              if (!defaultFn) return null;

              return row => {
                const audit = row.row as Row;
                const transitionSampleIdentifiers =
                  audit.transitionSamples?.map(ts => ts.transitionSampleIdentifier) || [];
                return transitionSampleIdentifiers.some(transitionSampleIdentifier =>
                  // @ts-ignore
                  defaultFn({ value: transitionSampleIdentifier, field: filterItem.field })
                );
              };
            },
          })),
        },
        {
          field: auditSampleId,
          headerName: t(auditSampleId),
          filterable: true,
          filterOperators: getGridStringOperators().map(operator => ({
            ...operator,
            getApplyFilterFn: (filterItem, _) => {
              // @ts-ignore
              const defaultFn = operator.getApplyFilterFn?.(filterItem, undefined as GridColDef);
              if (!defaultFn) return null;

              return row => {
                const audit = row.row as Row;
                const sampleIdentifiers = audit.transitionSamples?.map(ts => ts.sampleId) || [];
                return sampleIdentifiers.some(sampleIdentifier =>
                  // @ts-ignore
                  defaultFn({ value: sampleIdentifier, field: filterItem.field })
                );
              };
            },
          })),
        },
        {
          field: auditParentSampleId,
          headerName: t(auditParentSampleId),
          filterable: true,
          filterOperators: getGridStringOperators().map(operator => ({
            ...operator,
            getApplyFilterFn: (filterItem, _) => {
              // @ts-ignore
              const defaultFn = operator.getApplyFilterFn?.(filterItem, undefined as GridColDef);
              if (!defaultFn) return null;

              return row => {
                const audit = row.row as Row;
                const parentSampleIds =
                  audit.transitionSamples?.map(ts => ts.parentAuditTransitionSample?.sampleId) || [];
                return parentSampleIds.some(parentSampleId =>
                  // @ts-ignore
                  defaultFn({ value: parentSampleId, field: filterItem.field })
                );
              };
            },
          })),
        },
        {
          field: patientId,
          headerName: t(patientId),
          filterable: true,
          filterOperators: getGridStringOperators().map(operator => ({
            ...operator,
            getApplyFilterFn: (filterItem, _) => {
              // @ts-ignore
              const defaultFn = operator.getApplyFilterFn?.(filterItem, undefined as GridColDef);
              if (!defaultFn) return null;

              return row => {
                const audit = row.row as Row;
                const patientIds = audit.transitionSamples?.map(ts => ts.patientId) || [];
                return patientIds.some(patientId =>
                  // @ts-ignore
                  defaultFn({ value: patientId, field: filterItem.field })
                );
              };
            },
          })),
        },
        {
          field: patientDeidentity,
          headerName: t(patientDeidentity),
          filterable: true,
          filterOperators: getGridStringOperators().map(operator => ({
            ...operator,
            getApplyFilterFn: (filterItem, _) => {
              // @ts-ignore
              const defaultFn = operator.getApplyFilterFn?.(filterItem, undefined as GridColDef);
              if (!defaultFn) return null;

              return row => {
                const audit = row.row as Row;
                const patientDeids = audit.transitionSamples?.map(ts => ts.patientDeidentity) || [];
                return patientDeids.some(patientDeidentity =>
                  // @ts-ignore
                  defaultFn({ value: patientDeidentity, field: filterItem.field })
                );
              };
            },
          })),
        },
      ],
      []
    ) as GridColDef[];
  };

  const columns = useColumns();

  const [rows, setRows] = useState<GridRowsProp<Row>>([]);

  const getRows = useCallback((data: ReadonlyArray<AuditSampleTracking>) => {
    const rows: Row[] = data.map((r, i) => {
      const transitionChanges = FromTransitionDto(r.transition);
      const transitionId =
        r.transition?.transitionId === '00000000-0000-0000-0000-000000000000' ? undefined : r.transition?.transitionId;

      return {
        id: i + 1,
        auditSampleTrackingId: r.auditSampleTrackingId,
        transition: r.transition?.transition,
        transitionId: transitionId,
        transitionChanges: transitionChanges,
        auditType: r.type,
        auditAction: r.action,
        auditUser: r.user.userName || r.user.userId,
        auditEvent: r.createdAt,
        transitionSamples: r.transitionSamples,
        numTransitionSamples: r.transitionSamples.length,
      };
    });

    return rows;
  }, []);

  useEffect(() => {
    return LoadState(setLoadingState, async () => {
      if (!(accessToken && researchProjectId !== undefined)) {
        return;
      }

      const audits = await GetAuditsForResearchProject(researchProjectId, accessToken);

      setRows(getRows(audits));
    });
  }, [accessToken, researchProjectId, getRows]);

  const filterColumns = ({ field, columns, currentFilters }: FilterColumnsArgs) => {
    return columns.filter(colDef => colDef.filterable).map(column => column.field);
  };

  const getColumnForNewFilter = ({ currentFilters, columns }: GetColumnForNewFilterArgs) => {
    const columnForNewFilter = columns
      .filter(colDef => colDef.filterable)
      .find(colDef => colDef.filterOperators?.length);
    return columnForNewFilter?.field ?? null;
  };

  function CustomToolbar() {
    return (
      <FlexBox flexDirection={'row'}>
        <GridToolbarFilterButton />
      </FlexBox>
    );
  }

  return (
    <>
      {loadingState.status === 'Complete' && (
        <>
          <Box display='flex' flexDirection='row' alignItems='center' mb={1} justifyContent='space-between'>
            <Typography variant='subtitle1'>{t('auditSampleTrackingGridInfo')}</Typography>
          </Box>
          <DataGridPro
            columns={columns}
            rows={rows}
            getDetailPanelContent={({ row }) => <AuditRecordDetails row={row} />}
            getDetailPanelHeight={_ => 'auto'} // Height based on the content.
            getRowHeight={() => 'auto'}
            disableRowSelectionOnClick
            disableColumnReorder={true}
            initialState={{
              pinnedColumns: { left: [GRID_TREE_DATA_GROUPING_FIELD, copyBtn] },
            }}
            slots={{
              toolbar: CustomToolbar,
            }}
            slotProps={{
              filterPanel: {
                filterFormProps: {
                  filterColumns,
                },
                getColumnForNewFilter,
              },
            }}
            columnVisibilityModel={{
              transitionSampleIdentifier: false,
              sampleId: false,
              parentSampleId: false,
              patientId: false,
              patientDeidentity: false,
            }}
            sx={{
              '& .MuiDataGrid-cell': {
                py: 1.5,
              },
            }}
          />
        </>
      )}
      <LoadingIndicator type={'Circular'} loadingState={loadingState} margin={'LR'} />
      <ErrorIndicator loadingState={loadingState} />
    </>
  );
};
